import {
  FC,
  useState,
  useRef,
  useEffect,
  useCallback,
  KeyboardEvent,
} from 'react';
import { Box, Input, Text, VStack, HStack, IconButton } from '@chakra-ui/react';
import { FiX, FiChevronDown } from 'react-icons/fi';

/**
 * 検索可能セレクトボックスのオプション
 */
export interface SearchableSelectOption {
  /** 値（IDなど） */
  value: string;
  /** 表示ラベル */
  label: string;
  /** 検索用テキスト（名前、生体番号、登録番号などを結合） */
  searchText: string;
  /** サブラベル（任意） */
  subLabel?: string;
}

/**
 * SearchableSelectのプロパティ
 */
export interface SearchableSelectProps {
  /** 選択肢 */
  options: SearchableSelectOption[];
  /** 選択中の値 */
  value?: string;
  /** 値が変更されたときのコールバック */
  onChange: (value: string) => void;
  /** プレースホルダー */
  placeholder?: string;
  /** 新規作成を許可するか */
  allowCreate?: boolean;
  /** 無効化 */
  disabled?: boolean;
  /** 名前（react-hook-form用） */
  name?: string;
}

/**
 * 検索可能なセレクトボックスコンポーネント
 */
export const SearchableSelect: FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = '選択してください',
  allowCreate = false,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // 選択中のオプションを取得
  const selectedOption = options.find((opt) => opt.value === value);

  // __new__:プレフィックスから名前を抽出
  const getNewValueName = (val: string | undefined): string | null => {
    if (val && val.startsWith('__new__:')) {
      return val.replace('__new__:', '');
    }
    return null;
  };

  // 入力値を正規化して検索
  const normalizeForSearch = (text: string): string => {
    return text.toLowerCase().replace(/[\s\u3000]/g, '');
  };

  // フィルタリングされたオプション
  const filteredOptions = options.filter((option) => {
    if (!searchText) return true;
    const normalizedSearch = normalizeForSearch(searchText);
    const normalizedSearchText = normalizeForSearch(option.searchText);
    return normalizedSearchText.includes(normalizedSearch);
  });

  // 新規作成オプションを表示するかどうか
  const showCreateOption =
    allowCreate &&
    searchText.trim() !== '' &&
    filteredOptions.length === 0;

  // クリック外でドロップダウンを閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        // 選択中の値があればそのラベルに戻す
        if (selectedOption) {
          setSearchText(selectedOption.label);
        } else {
          const newName = getNewValueName(value);
          if (newName) {
            setSearchText(newName);
          } else {
            setSearchText('');
          }
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedOption, value]);

  // 選択値が変わったときに入力欄を更新
  useEffect(() => {
    if (selectedOption) {
      setSearchText(selectedOption.label);
    } else {
      const newName = getNewValueName(value);
      if (newName) {
        // 新規作成オプションの場合、プレフィックスを除去して表示
        setSearchText(newName);
      } else if (!value) {
        setSearchText('');
      }
    }
  }, [value, selectedOption]);

  // オプション選択
  const handleSelect = useCallback(
    (optionValue: string) => {
      onChange(optionValue);
      setIsOpen(false);
      setHighlightedIndex(-1);
      const selected = options.find((opt) => opt.value === optionValue);
      if (selected) {
        setSearchText(selected.label);
      }
    },
    [onChange, options]
  );

  // 新規作成選択
  const handleCreateSelect = useCallback(() => {
    const newValue = `__new__:${searchText.trim()}`;
    onChange(newValue);
    setIsOpen(false);
    setHighlightedIndex(-1);
  }, [onChange, searchText]);

  // クリア
  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange('');
      setSearchText('');
      setIsOpen(false);
    },
    [onChange]
  );

  // キーボード操作
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const totalItems = showCreateOption
      ? filteredOptions.length + 1
      : filteredOptions.length;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        }
        setHighlightedIndex((prev) => Math.min(prev + 1, totalItems - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          if (highlightedIndex < filteredOptions.length) {
            handleSelect(filteredOptions[highlightedIndex].value);
          } else if (showCreateOption) {
            handleCreateSelect();
          }
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        if (selectedOption) {
          setSearchText(selectedOption.label);
        } else {
          const newName = getNewValueName(value);
          if (newName) {
            setSearchText(newName);
          } else {
            setSearchText('');
          }
        }
        break;
    }
  };

  // インプットクリック時
  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(true);
      setSearchText('');
    }
  };

  // 入力変更
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  return (
    <Box position="relative" ref={containerRef}>
      <HStack gap={0}>
        <Input
          ref={inputRef}
          value={searchText}
          onChange={handleInputChange}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          borderRightRadius={value ? 0 : undefined}
          pr={value ? 2 : 10}
        />
        {value && (
          <IconButton
            aria-label="クリア"
            size="sm"
            variant="ghost"
            onClick={handleClear}
            borderLeftRadius={0}
            borderRightRadius="md"
            h="40px"
          >
            <FiX />
          </IconButton>
        )}
        {!value && (
          <Box
            position="absolute"
            right={3}
            top="50%"
            transform="translateY(-50%)"
            pointerEvents="none"
          >
            <FiChevronDown />
          </Box>
        )}
      </HStack>

      {isOpen && !disabled && (
        <Box
          ref={listRef}
          position="absolute"
          top="100%"
          left={0}
          right={0}
          zIndex={1000}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="lg"
          maxH="200px"
          overflowY="auto"
          mt={1}
        >
          <VStack gap={0} align="stretch">
            {filteredOptions.length === 0 && !showCreateOption && (
              <Box p={3} color="gray.500">
                <Text fontSize="sm">該当なし</Text>
              </Box>
            )}

            {filteredOptions.map((option, index) => (
              <Box
                key={option.value}
                p={3}
                cursor="pointer"
                bg={highlightedIndex === index ? 'blue.50' : 'transparent'}
                _hover={{ bg: 'gray.50' }}
                onClick={() => handleSelect(option.value)}
              >
                <Text fontSize="sm" fontWeight="medium">
                  {option.label}
                </Text>
                {option.subLabel && (
                  <Text fontSize="xs" color="gray.500">
                    {option.subLabel}
                  </Text>
                )}
              </Box>
            ))}

            {showCreateOption && (
              <Box
                p={3}
                cursor="pointer"
                bg={
                  highlightedIndex === filteredOptions.length
                    ? 'blue.50'
                    : 'transparent'
                }
                _hover={{ bg: 'green.50' }}
                onClick={handleCreateSelect}
                borderTop={filteredOptions.length > 0 ? '1px solid' : undefined}
                borderColor="gray.200"
              >
                <Text fontSize="sm" color="green.600" fontWeight="medium">
                  「{searchText.trim()}」を新規登録
                </Text>
              </Box>
            )}
          </VStack>
        </Box>
      )}
    </Box>
  );
};

export default SearchableSelect;
